/**
 * 充值弹窗: 选金额 → 创建订单 → 展示微信支付二维码 (code_url) → 每 2s 轮询直到 paid / failed。
 * 网关未配置微信支付时 code_url 为空, 提示 "支付通道未配置", 不轮询。
 */
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { errorDetail } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { Alert, Button, Dialog, Field, Input } from "@/components/ui";
import { useCreateRecharge, useInvalidateBalance, useRechargeStatus } from "../hooks";
import type { RechargeCreateResponse } from "../api";
import { fenToYuan } from "../utils";

const PRESETS = [10, 50, 100];

export function RechargeModal({ onClose }: { onClose: () => void }) {
  const createMut = useCreateRecharge();
  const invalidate = useInvalidateBalance();

  const [preset, setPreset] = useState<number | "custom">(50);
  const [custom, setCustom] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [order, setOrder] = useState<RechargeCreateResponse | null>(null);

  const channelReady = !!order && order.pay_channel_ready && !!order.code_url;
  // 只有能扫码时才轮询; 弹窗关闭即卸载, 轮询自然停止
  const status = useRechargeStatus(channelReady ? order!.merchant_order_no : null);
  const current = status.data?.status ?? order?.status;

  useEffect(() => {
    if (current === "paid" || current === "failed") invalidate();
  }, [current]);

  const amount = preset === "custom" ? Number(custom) : preset;

  const submit = async () => {
    setErr(null);
    if (!Number.isFinite(amount) || amount <= 0) {
      setErr("请输入正确的金额");
      return;
    }
    if (Math.round(amount * 100) !== amount * 100) {
      setErr("金额最多两位小数");
      return;
    }
    try {
      setOrder(await createMut.mutateAsync(amount));
    } catch (e) {
      setErr(errorDetail(e, "创建订单失败"));
    }
  };

  const chip = (active: boolean) =>
    cn(
      "h-10 rounded-lg border text-sm font-medium tabular-nums transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      active ? "border-brand bg-primary-soft text-primary ring-1 ring-brand" : "bg-card text-foreground hover:bg-muted",
    );

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="余额充值"
      description={order ? undefined : "选择充值金额，使用微信扫码支付"}
      footer={
        !order ? (
          <>
            <Button variant="outline" onClick={onClose}>取消</Button>
            <Button onClick={submit} loading={createMut.isPending}>去支付</Button>
          </>
        ) : (
          <Button variant={current === "paid" ? "primary" : "outline"} onClick={onClose}>
            {current === "paid" ? "完成" : "关闭"}
          </Button>
        )
      }
    >
      {!order ? (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2" role="group" aria-label="充值金额">
            {PRESETS.map((p) => (
              <button key={p} type="button" aria-pressed={preset === p} onClick={() => setPreset(p)} className={chip(preset === p)}>
                ¥{p}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={preset === "custom"}
              onClick={() => setPreset("custom")}
              className={chip(preset === "custom")}
            >
              自定义
            </button>
          </div>
          {preset === "custom" && (
            <Field label="金额 (元)">
              <Input
                type="number"
                min={0.01}
                step={0.01}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="例如 20"
                autoFocus
              />
            </Field>
          )}
          {err && <Alert>{err}</Alert>}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <div className="text-sm text-muted-foreground">
            充值金额{" "}
            <span className="text-xl font-semibold tabular-nums text-price">¥{fenToYuan(order.amount_fen)}</span>
          </div>
          {current === "paid" ? (
            <>
              <CheckCircle2 className="h-14 w-14 text-success" />
              <div className="text-sm font-medium text-foreground">
                支付成功
                {status.data && <>，当前余额 ¥{fenToYuan(status.data.balance_fen)}</>}
              </div>
            </>
          ) : current === "failed" ? (
            <>
              <XCircle className="h-14 w-14 text-danger" />
              <div className="text-sm text-danger-strong">支付失败，请重新下单</div>
            </>
          ) : !channelReady ? (
            <Alert variant="warning" className="w-full text-left">支付通道未配置，请联系平台管理员</Alert>
          ) : (
            <>
              <div className="rounded-xl border bg-white p-3 shadow-sm">
                <QRCodeSVG value={order.code_url!} size={192} />
              </div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                请使用微信扫码支付，支付后自动到账
              </div>
            </>
          )}
          <div className="font-mono text-xs text-muted-foreground">{order.merchant_order_no}</div>
        </div>
      )}
    </Dialog>
  );
}
